import { getTcGroups } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './use-current-user';
/**
 * Canonical "may this user use the teleconsultation worklist?" signal, shared by
 * the top nav, the worklist page, and the sidebar-count poller so they can never
 * disagree.
 *
 * The backend gate (RequireGroupMemberAsync) admits a caller when they have at
 * least one active `consultant_group_member` row, OR they are a super admin (JWT
 * claim). We mirror that here off GET /teleconsultation/groups, which returns the
 * caller's active member groups (all groups for a super admin) and — crucially —
 * returns 200 with an empty array for a non-member rather than a 403. Reading the
 * group list therefore tells us membership without ever tripping the very 403 the
 * gate exists to avoid.
 *
 * This replaces the earlier signal that derived membership from the heavier
 * /teleconsultation/profile response: the profile endpoint is not the membership
 * gate, and coupling entitlement to it let non-members keep polling the worklist.
 */
export function useIsTcMember() {
    const { isSuperAdmin, isLoading: isUserLoading } = useCurrentUser();
    // Same query key/options as useEffectiveTcGroups's group fetch (use-tc-groups.tsx),
    // so the two observers share one cached request instead of issuing duplicates.
    const { data: groups, isLoading: isGroupsLoading } = useQuery({
        queryKey: ['tc-groups'],
        queryFn: getTcGroups,
        staleTime: 5 * 60_000,
    });
    const isTcMember = (groups?.length ?? 0) > 0;
    const canAccessTeleconsultation = isSuperAdmin || isTcMember;
    // Only "loading" while the answer is genuinely unknown: the identity is still
    // resolving, or a non-super-admin is still waiting on the group list. Once we
    // can already say "yes" (super admin, or groups arrived), stop reporting loading
    // so entitled users never see a spurious spinner.
    const isLoading = isUserLoading || (!canAccessTeleconsultation && isGroupsLoading);
    return { isTcMember, isSuperAdmin, canAccessTeleconsultation, isLoading };
}
